import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { FormField } from "../shared/FormField";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

export function MoveDeliveryDialog({ open, onOpenChange, delivery, currentRouteId, date, onDone }) {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetRouteId, setTargetRouteId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !delivery) return;
    setTargetRouteId("");
    setLoading(true);
    apiClient.get("/routes", { params: { date: date || undefined, limit: 200 } })
      .then(({ data }) => setRoutes(data.items.filter((r) => r.id !== currentRouteId)))
      .catch(() => setRoutes([]))
      .finally(() => setLoading(false));
  }, [open, delivery, date, currentRouteId]);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/deliveries/${delivery.id}/reassign-route`, { new_route_id: Number(targetRouteId) });
      toast.success(`Delivery #${delivery.id} moved to route #${targetRouteId}`);
      onDone();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not move this delivery."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="move-delivery-dialog">
        <DialogHeader>
          <DialogTitle>Move Delivery #{delivery?.id}</DialogTitle>
          <DialogDescription>
            {currentRouteId
              ? "Choose a route to move this stop to. Both routes' totals update automatically."
              : "Assign this unassigned delivery to an existing route."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <Skeleton className="h-9 w-full" />
        ) : routes.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="move-delivery-no-routes">
            No other routes exist{date ? ` for ${date}` : ""}. Create a route first.
          </p>
        ) : (
          <FormField label="Target Route" testId="move-delivery-route-field">
            <Select value={targetRouteId} onValueChange={setTargetRouteId}>
              <SelectTrigger data-testid="move-delivery-route-select"><SelectValue placeholder="Select a route..." /></SelectTrigger>
              <SelectContent>
                {routes.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    Route #{r.id} — {r.driver_name || "Unassigned"} ({r.deliveries_done}/{r.deliveries_total})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="move-delivery-cancel-button">Cancel</Button>
          <Button variant="accent" disabled={saving || !targetRouteId} onClick={handleConfirm} data-testid="move-delivery-confirm-button">
            {saving ? "Moving..." : "Move Delivery"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
